import React, { useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { ArrowRight, ArrowLeft, ChevronDown } from "lucide-react";

export const ValuatorStep2 = ({ vf, onBack, onNext }) => {
  const { t } = useLang();
  const [showVehicleAdv, setShowVehicleAdv] = useState(false);
  const { form, update, isAppliance, isBook, isPC, isRepairable, hasUsageHours, hasMaterial, isMotorVehicle } = vf;
  const pcTypeOptions = form.category === "electronics_laptop"
    ? ["desktop", "laptop"]
    : ["gpu", "cpu", "ram", "storage", "motherboard", "psu"];

  return (
    <div data-testid="wizard-step-2" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className={`${(isRepairable || hasUsageHours) ? "lg:col-span-6" : "lg:col-span-12"} bg-[#F5F5F5] border border-black p-8`}>
        <span className="overline">02 / {t.valuator.section_details}</span>

        <div className="mt-6 space-y-3">
          {[
            { k: "has_box", label: t.valuator.has_box },
            { k: "has_accessories", label: t.valuator.has_accessories },
            { k: "has_warranty", label: t.valuator.has_warranty },
            { k: "has_certificate", label: t.valuator.has_certificate },
            { k: "has_verifiable_serial", label: t.valuator.has_verifiable_serial },
          ].map((opt) => (
            <label key={opt.k} className="flex items-start gap-3 cursor-pointer group">
              <input
                data-testid={`form-${opt.k}`}
                type="checkbox"
                checked={form[opt.k]}
                onChange={(e) => update(opt.k, e.target.checked)}
                className="w-5 h-5 mt-0.5 flex-shrink-0 border-2 border-black accent-black"
              />
              <span className="font-medium text-sm">{opt.label}</span>
            </label>
          ))}
        </div>

        <div className="mt-5">
          <label className="label-rv">{t.valuator.rarity}</label>
          <select data-testid="form-rarity" className="input-rv" value={form.rarity} onChange={(e) => update("rarity", e.target.value)}>
            <option value="">{t.valuator.optional_select}</option>
            {["common", "out_of_production", "limited_edition", "rare_variant"].map((r) => (
              <option key={r} value={r}>{t.valuator.rarities[r]}</option>
            ))}
          </select>
        </div>

        <div className="mt-5">
          <label className="label-rv">{t.valuator.seasonality}</label>
          <select data-testid="form-seasonality" className="input-rv" value={form.seasonality} onChange={(e) => update("seasonality", e.target.value)}>
            <option value="">{t.valuator.optional_select}</option>
            {["none", "summer", "winter"].map((s) => (
              <option key={s} value={s}>{t.valuator.seasonalities[s]}</option>
            ))}
          </select>
        </div>

        {isAppliance && (
          <div className="mt-5" data-testid="energy-class-block">
            <label className="label-rv">{t.valuator.energy_class}</label>
            <select data-testid="form-energy-class" className="input-rv" value={form.energy_class} onChange={(e) => update("energy_class", e.target.value)}>
              <option value="">{t.valuator.optional_select}</option>
              {["A", "B", "C", "D", "E", "F", "G"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        {hasMaterial && (
          <div className="mt-5" data-testid="material-block">
            <label className="label-rv">{t.valuator.material}</label>
            <input data-testid="form-material" name="material" className="input-rv" value={form.material} onChange={(e) => update("material", e.target.value)} placeholder="oro 18kt, pelle vera, cotone..." />
            <p className="text-[11px] text-[#737373] mt-1">{t.valuator.material_help}</p>
          </div>
        )}
      </div>

      {(isRepairable || hasUsageHours) && (
        <div className="lg:col-span-6 bg-white border border-black p-8">
          {isRepairable && (
            <div data-testid="repair-block">
              <label className="label-rv">{t.valuator.repair_status}</label>
              <select data-testid="form-repair-status" className="input-rv" value={form.repair_status} onChange={(e) => update("repair_status", e.target.value)}>
                <option value="">{t.valuator.optional_select}</option>
                {["never", "repaired_original", "repaired_compatible"].map((r) => (
                  <option key={r} value={r}>{t.valuator.repair_statuses[r]}</option>
                ))}
              </select>
              {(form.repair_status === "repaired_original" || form.repair_status === "repaired_compatible") && (
                <div className="mt-3" data-testid="repair-details-block">
                  <label className="label-rv">{t.valuator.repair_details}</label>
                  <input data-testid="form-repair-details" className="input-rv" value={form.repair_details} onChange={(e) => update("repair_details", e.target.value)} placeholder={t.valuator.repair_details_ph} />
                </div>
              )}
            </div>
          )}

          {hasUsageHours && (
            <div className={isRepairable ? "mt-5" : ""} data-testid="usage-hours-block">
              <label className="label-rv">{t.valuator.usage_hours}</label>
              <input data-testid="form-usage-hours" name="usage_hours" type="number" min="0" className="input-rv" value={form.usage_hours} onChange={(e) => update("usage_hours", e.target.value)} placeholder="1200" />
              <p className="text-[11px] text-[#737373] mt-1">{t.valuator.usage_hours_help}</p>
            </div>
          )}
        </div>
      )}

      {/* VEHICLE ADVANCED */}
      {isMotorVehicle && (
        <div className="lg:col-span-12 bg-white border border-black" data-testid="vehicle-adv-panel">
          <button
            type="button"
            data-testid="vehicle-adv-toggle"
            onClick={() => setShowVehicleAdv((s) => !s)}
            className="w-full flex items-center justify-between p-6 hover:bg-[#F5F5F5] transition-colors"
          >
            <div className="text-left">
              <span className="overline">02B / {t.valuator.vehicle_adv}</span>
              <p className="text-xs text-[#737373] mt-1">{t.valuator.vehicle_adv_hint}</p>
            </div>
            <ChevronDown size={18} className={`flex-shrink-0 transition-transform ${showVehicleAdv ? "rotate-180" : ""}`} />
          </button>
          {showVehicleAdv && (
            <div data-testid="vehicle-adv-fields" className="px-6 pb-8 pt-6 grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-black/10">
              <div>
                <label className="label-rv">{t.valuator.service_history}</label>
                <select data-testid="form-service-history" className="input-rv" value={form.service_history} onChange={(e) => update("service_history", e.target.value)}>
                  <option value="">{t.valuator.optional_select}</option>
                  {["full", "partial", "none"].map((s) => <option key={s} value={s}>{t.valuator.service_histories[s]}</option>)}
                </select>
              </div>
              <div>
                <label className="label-rv">{t.valuator.recent_maintenance}</label>
                <input data-testid="form-recent-maintenance" className="input-rv" value={form.recent_maintenance} onChange={(e) => update("recent_maintenance", e.target.value)} placeholder={t.valuator.recent_maintenance_ph} />
              </div>
              <div>
                <label className="label-rv">{t.valuator.body_condition}</label>
                <select data-testid="form-body-condition" className="input-rv" value={form.body_condition} onChange={(e) => update("body_condition", e.target.value)}>
                  <option value="">{t.valuator.optional_select}</option>
                  {["excellent", "good", "fair", "poor"].map((s) => <option key={s} value={s}>{t.valuator.vehicle_conditions[s]}</option>)}
                </select>
              </div>
              <div>
                <label className="label-rv">{t.valuator.interior_condition}</label>
                <select data-testid="form-interior-condition" className="input-rv" value={form.interior_condition} onChange={(e) => update("interior_condition", e.target.value)}>
                  <option value="">{t.valuator.optional_select}</option>
                  {["excellent", "good", "fair", "poor"].map((s) => <option key={s} value={s}>{t.valuator.vehicle_conditions[s]}</option>)}
                </select>
              </div>
              <div>
                <label className="label-rv">{t.valuator.electronics_ok}</label>
                <select data-testid="form-electronics-ok" className="input-rv" value={form.electronics_ok} onChange={(e) => update("electronics_ok", e.target.value)}>
                  <option value="">{t.valuator.optional_select}</option>
                  <option value="yes">{t.valuator.electronics_options.yes}</option>
                  <option value="no">{t.valuator.electronics_options.no}</option>
                </select>
              </div>
              <div>
                <label className="label-rv">{t.valuator.euro_class}</label>
                <select data-testid="form-euro-class" className="input-rv" value={form.euro_class} onChange={(e) => update("euro_class", e.target.value)}>
                  <option value="">{t.valuator.optional_select}</option>
                  {["euro3", "euro4", "euro5", "euro6"].map((c) => <option key={c} value={c}>{c.replace("euro", "Euro ")}</option>)}
                </select>
              </div>
              <div>
                <label className="label-rv">{t.valuator.fuel_type}</label>
                <select data-testid="form-fuel-type" className="input-rv" value={form.fuel_type} onChange={(e) => update("fuel_type", e.target.value)}>
                  <option value="">{t.valuator.optional_select}</option>
                  {Object.entries(t.valuator.fuel_types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {form.fuel_type === "electric" && (
                <div data-testid="battery-soh-block">
                  <label className="label-rv">{t.valuator.battery_soh}</label>
                  <input data-testid="form-battery-soh" type="number" min="0" max="100" className="input-rv" value={form.battery_soh} onChange={(e) => update("battery_soh", e.target.value)} placeholder="92" />
                </div>
              )}
              <div>
                <label className="label-rv">{t.valuator.power_kw}</label>
                <input data-testid="form-power-kw" type="number" min="0" className="input-rv" value={form.power_kw} onChange={(e) => update("power_kw", e.target.value)} placeholder="85" />
                <p className="text-[11px] text-[#737373] mt-1">{t.valuator.power_kw_help}</p>
              </div>
              <div>
                <label className="label-rv">{t.valuator.owners_count}</label>
                <input data-testid="form-owners-count" type="number" min="1" className="input-rv" value={form.owners_count} onChange={(e) => update("owners_count", e.target.value)} placeholder="1" />
              </div>
              <label className="flex items-center gap-3 cursor-pointer md:col-span-2">
                <input data-testid="form-recent-inspection" type="checkbox" checked={form.recent_inspection} onChange={(e) => update("recent_inspection", e.target.checked)} className="w-5 h-5 border-2 border-black accent-black" />
                <span className="font-medium text-sm">{t.valuator.recent_inspection}</span>
              </label>
            </div>
          )}
        </div>
      )}

      {/* BOOK DETAILS */}
      {isBook && (
        <div className="lg:col-span-12 bg-[#F5F5F5] border border-black p-8" data-testid="book-details-panel">
          <span className="overline">02B / {t.valuator.book_details}</span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
            <div>
              <label className="label-rv">{t.valuator.book_edition}</label>
              <select data-testid="form-book-edition" className="input-rv" value={form.book_edition} onChange={(e) => update("book_edition", e.target.value)}>
                <option value="">{t.valuator.optional_select}</option>
                {Object.entries(t.valuator.book_editions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="space-y-3">
              {[
                { k: "is_signed", label: t.valuator.is_signed },
                { k: "out_of_print", label: t.valuator.out_of_print },
                { k: "has_dust_jacket", label: t.valuator.has_dust_jacket },
                { k: "has_annotations", label: t.valuator.has_annotations },
              ].map((opt) => (
                <label key={opt.k} className="flex items-center gap-3 cursor-pointer">
                  <input data-testid={`form-${opt.k}`} type="checkbox" checked={form[opt.k]} onChange={(e) => update(opt.k, e.target.checked)} className="w-5 h-5 border-2 border-black accent-black" />
                  <span className="font-medium text-sm">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PC & HARDWARE SPECS */}
      {isPC && (
        <div className="lg:col-span-12 bg-[#F5F5F5] border border-black p-8" data-testid="pc-specs-panel">
          <span className="overline">02C / {t.valuator.pc_specs_section}</span>
          <p className="text-xs text-[#737373] mt-1">{t.valuator.pc_specs_hint}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
            <div>
              <label className="label-rv">{t.valuator.pc_component_type}</label>
              <select data-testid="form-pc-component-type" className="input-rv" value={form.pc_component_type} onChange={(e) => update("pc_component_type", e.target.value)}>
                <option value="">{t.valuator.optional_select}</option>
                {pcTypeOptions.map((k) => <option key={k} value={k}>{t.valuator.pc_types[k]}</option>)}
              </select>
            </div>

            {form.pc_component_type === "gpu" && (
              <div>
                <label className="label-rv">{t.valuator.vram}</label>
                <input data-testid="form-vram" type="text" className="input-rv" placeholder={t.valuator.vram_ph} value={form.vram} onChange={(e) => update("vram", e.target.value)} />
              </div>
            )}

            {form.pc_component_type === "gpu" && (
              <div>
                <label className="label-rv">{t.valuator.gpu_usage}</label>
                <select data-testid="form-gpu-usage" className="input-rv" value={form.gpu_usage} onChange={(e) => update("gpu_usage", e.target.value)}>
                  <option value="">{t.valuator.optional_select}</option>
                  {Object.entries(t.valuator.gpu_usages).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            )}

            {form.pc_component_type === "cpu" && (
              <div>
                <label className="label-rv">{t.valuator.cpu_socket}</label>
                <input data-testid="form-cpu-socket" type="text" className="input-rv" placeholder={t.valuator.cpu_socket_ph} value={form.cpu_socket} onChange={(e) => update("cpu_socket", e.target.value)} />
              </div>
            )}

            {(form.pc_component_type === "ram" || form.pc_component_type === "storage") && (
              <div>
                <label className="label-rv">{t.valuator.ram_storage_spec}</label>
                <input data-testid="form-ram-storage-spec" type="text" className="input-rv" placeholder={t.valuator.ram_storage_ph} value={form.ram_storage_spec} onChange={(e) => update("ram_storage_spec", e.target.value)} />
              </div>
            )}

            {(form.pc_component_type === "desktop" || form.pc_component_type === "laptop") && (
              <div className="md:col-span-2">
                <label className="label-rv">{t.valuator.pc_build_specs}</label>
                <input data-testid="form-pc-build-specs" type="text" className="input-rv" placeholder={t.valuator.pc_build_specs_ph} value={form.pc_build_specs} onChange={(e) => update("pc_build_specs", e.target.value)} />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="lg:col-span-12 hidden lg:flex items-center justify-between">
        <button type="button" data-testid="goto-step-1" onClick={onBack} className="btn-outline">
          <ArrowLeft size={16} /> {t.valuator.step_back}
        </button>
        <button type="button" data-testid="goto-step-3" onClick={onNext} className="btn-primary">
          {t.valuator.step_next} <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default ValuatorStep2;
